import { z } from "zod";
import { localizedTextSchema } from "./intake-template";

// TES-212 — the Wellness Plan: a compact Home section of three dimensions
// (nutrition / movement / mind). Each dimension carries a current focus + a next
// small step (the "what changes week-to-week", per the board) — NOT the whole
// plan (recipes/workouts live elsewhere). Admin-authored + tenant-scoped, same
// shape as the JourneyPhase model. Progress framing is intentionally light.

export const WELLNESS_DIMENSIONS = ["nutrition", "movement", "mind"] as const;
export const wellnessDimensionSchema = z.enum(WELLNESS_DIMENSIONS);
export type WellnessDimension = z.infer<typeof wellnessDimensionSchema>;

export const WELLNESS_STATUSES = ["draft", "published", "archived"] as const;
export const wellnessStatusSchema = z.enum(WELLNESS_STATUSES);
export type WellnessStatus = z.infer<typeof wellnessStatusSchema>;

// Authoring DTO (admin) — the full stored focus definition for one dimension.
export const wellnessFocusSchema = z.object({
  id: z.string().uuid(),
  dimension: wellnessDimensionSchema,
  emoji: z.string().nullable(),
  title: localizedTextSchema,
  focus: localizedTextSchema,
  nextStep: localizedTextSchema,
  // TES-220/221/222 — progression framing (e.g. "Vecka 2 av 6"). Both null =
  // no progression shown. Cohort-level, authored (the plan content stays
  // stable; the week is what moves).
  weekCurrent: z.number().int().positive().nullable(),
  weekTotal: z.number().int().positive().nullable(),
  // The card CTA into deeper pillar content. Both must be present to render.
  ctaLabel: localizedTextSchema.nullable(),
  ctaRoute: z.string().nullable(),
  status: wellnessStatusSchema,
});
export type WellnessFocus = z.infer<typeof wellnessFocusSchema>;

// Both locales required + non-empty: a published card must never render blank.
const nonEmptyLocalizedSchema = z.object({
  sv: z.string().min(1),
  en: z.string().min(1),
});

// Create/update payload (admin). `dimension` is the identity (one row each).
export const upsertWellnessFocusSchema = z
  .object({
    dimension: wellnessDimensionSchema,
    emoji: z.string().max(8).nullable().optional(),
    title: nonEmptyLocalizedSchema,
    focus: nonEmptyLocalizedSchema,
    nextStep: nonEmptyLocalizedSchema,
    // Progression: either both weeks or neither; current must not exceed total.
    weekCurrent: z.number().int().positive().nullable().optional(),
    weekTotal: z.number().int().positive().nullable().optional(),
    // CTA: either both label + route or neither.
    ctaLabel: nonEmptyLocalizedSchema.nullable().optional(),
    ctaRoute: z.string().min(1).max(200).nullable().optional(),
    status: wellnessStatusSchema.default("draft"),
  })
  .superRefine((v, ctx) => {
    const hasCurrent = v.weekCurrent != null;
    const hasTotal = v.weekTotal != null;
    if (hasCurrent !== hasTotal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasCurrent ? "weekTotal" : "weekCurrent"],
        message: "weekCurrent and weekTotal must be set together",
      });
    }
    if (hasCurrent && hasTotal && v.weekCurrent! > v.weekTotal!) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["weekCurrent"],
        message: "weekCurrent must not exceed weekTotal",
      });
    }
    const hasLabel = v.ctaLabel != null;
    const hasRoute = v.ctaRoute != null;
    if (hasLabel !== hasRoute) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasLabel ? "ctaRoute" : "ctaLabel"],
        message: "ctaLabel and ctaRoute must be set together",
      });
    }
  });
export type UpsertWellnessFocus = z.infer<typeof upsertWellnessFocusSchema>;

// ---------------------------------------------------------------------------
// Patient-facing DTO — locale-resolved, published dimensions only.
// ---------------------------------------------------------------------------

export const patientWellnessCardSchema = z.object({
  dimension: wellnessDimensionSchema,
  emoji: z.string().nullable(),
  title: z.string(),
  focus: z.string(),
  nextStep: z.string(),
  // TES-220/221/222 — progression (both weeks present) or null.
  week: z
    .object({
      current: z.number().int().positive(),
      total: z.number().int().positive(),
    })
    .nullable(),
  // The card CTA (label locale-resolved + route) or null.
  cta: z.object({ label: z.string(), route: z.string() }).nullable(),
});
export type PatientWellnessCard = z.infer<typeof patientWellnessCardSchema>;

// The Wellness Plan section: the published dimension cards + the patient's
// emphasized dimension (TES-223). Cards are ordered with `priority` first, then
// canonical order (nutrition → movement → mind). Empty when nothing is published.
export const patientWellnessSchema = z.object({
  cards: z.array(patientWellnessCardSchema),
  // The dimension the patient chose to emphasize, or null for an even layout.
  priority: wellnessDimensionSchema.nullable(),
});
export type PatientWellness = z.infer<typeof patientWellnessSchema>;

// TES-223 — set/clear the emphasized dimension. `dimension: null` resets to the
// even three-card layout.
export const setWellnessPrioritySchema = z.object({
  dimension: wellnessDimensionSchema.nullable(),
});
export type SetWellnessPriority = z.infer<typeof setWellnessPrioritySchema>;
